import { Injectable } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { Destroyer } from '@apps/tools';

export type LayoutState =
    | 'XSmall'
    | 'Small'
    | 'Medium'
    | 'Large'
    | 'XLarge'
    | 'Handset'
    | 'Tablet'
    | 'Web'
    | 'HandsetPortrait'
    | 'TabletPortrait'
    | 'WebPortrait'
    | 'HandsetLandscape'
    | 'TabletLandscape'
    | 'WebLandscape';

export type WidthState = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const LAYOUT = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
};

@Injectable({
    providedIn: 'root',
})
export class LayoutService extends Destroyer {
    stateSubject: BehaviorSubject<LayoutState[]> = new BehaviorSubject<
        LayoutState[]
    >([]);
    state$ = this.stateSubject.asObservable();

    widthSubject: BehaviorSubject<WidthState> = new BehaviorSubject<WidthState>(
        undefined
    );
    width$ = this.widthSubject.asObservable();

    constructor(breakpointObserver: BreakpointObserver) {
        super();

        Object.keys(Breakpoints).forEach(key => {
            breakpointObserver
                .observe(Breakpoints[key])
                .pipe(takeUntil(this.destroy$))
                .subscribe(e => this.updateState(key, e.matches));
        });

        breakpointObserver
            .observe(`(max-width: ${LAYOUT.sm - 1}px)`)
            .pipe(takeUntil(this.destroy$))
            .subscribe(e => this.updateWidth('xs', e.matches));

        breakpointObserver
            .observe(
                `(min-width: ${LAYOUT.sm}px) and (max-width: ${LAYOUT.md -
                    1}px)`
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(e => this.updateWidth('sm', e.matches));

        breakpointObserver
            .observe(
                `(min-width: ${LAYOUT.md}px) and (max-width: ${LAYOUT.lg -
                    1}px)`
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(e => this.updateWidth('md', e.matches));

        breakpointObserver
            .observe(
                `(min-width: ${LAYOUT.lg}px) and (max-width: ${LAYOUT.xl -
                    1}px)`
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(e => this.updateWidth('lg', e.matches));

        breakpointObserver
            .observe(`(min-width: ${LAYOUT.xl}px)`)
            .pipe(takeUntil(this.destroy$))
            .subscribe(e => this.updateWidth('lg', e.matches));
    }

    private updateState(name: string, add: boolean) {
        const value = this.stateSubject.getValue();
        if (add && value.findIndex(e => e === name) === -1) {
            value.push(name as LayoutState);
        }
        if (!add && value.findIndex(e => e === name) !== -1) {
            const index = value.findIndex(e => e === name);
            value.splice(index, 1);
        }
        this.stateSubject.next(value);
    }

    private updateWidth(name: WidthState, add: boolean) {
        if (add) {
            this.widthSubject.next(name);
        }
    }
}
